/**
 * Utility for formatting workflow explanations for chat display
 *
 * Cleans up technical step IDs and verbose boundary detection messages
 * to provide user-friendly, numbered operation summaries.
 */

/**
 * Format workflow explanations from backend for user-friendly chat display
 *
 * Transforms technical explanations like:
 *   "step_0_boundary_detection: Boundary Detection (Very High Confidence: 95%):
 *    Single header row at line 2. Data rows 3-40 (38 rows).
 *    The CSV content shows a clear structure where line 2 contains..."
 *
 * Into clean, numbered summaries like:
 *   "1. Identified data table structure
 *    2. Filled down values in columns [SalesMonth, CustNo]"
 *
 * @param explanations Array of raw explanation strings from backend
 * @returns Formatted string with numbered, user-friendly operation descriptions
 */
export function formatWorkflowExplanationsForChat(
  explanations: string[],
): string {
  if (!explanations || explanations.length === 0) {
    return 'Operations completed successfully.';
  }

  const formattedSteps = explanations
    .map((explanation, index) => {
      // Remove technical step ID prefix (e.g., "step_0_boundary_detection: " or "step_1: ")
      let cleaned = explanation.replace(/^step_\d+[^:]*:\s*/i, '').trim();

      // Special handling for boundary detection - make it user-friendly
      if (cleaned.includes('Boundary Detection')) {
        // Remove the "Boundary Detection (Confidence: X%):" header
        cleaned = cleaned.replace(/^Boundary Detection \([^)]+\):\s*/i, '');

        // Extract just the first sentence (the friendly summary)
        // This gets "Found column headers with 38 data rows" and drops the technical details
        const firstSentence = cleaned.split(/\.\s+/)[0];
        return `${index + 1}. ${firstSentence}.`;
      }

      // For other operations, take the first sentence to keep it concise
      const firstSentence = cleaned.split(/[.!?]\s+/)[0];
      return `${index + 1}. ${firstSentence}${firstSentence.endsWith('.') ? '' : '.'}`;
    })
    .join('\n');

  return formattedSteps;
}
